import { useReducer } from 'react';

import type { AccordionProps, OnChangeValue } from './Accordion.js';

/** @public */
export type SingleExpanded = string | number;

/** @public */
export type MultiExpanded = Array<string | number>;

/** @internal */
export const enum AccordionActions {
  TOGGLE_SECTION = 'toggle-section',
  SET_MULTIPLE = 'set-multiple',
}

/** @internal */
export type AccordionReducerActions =
  | {
      type: AccordionActions.TOGGLE_SECTION;
      payload: string | number;
    }
  | {
      type: AccordionActions.SET_MULTIPLE;
      payload: boolean;
    };

interface useAccordionStateProps<Multiple extends boolean> {
  expanded: AccordionProps<Multiple>['expanded'];
  defaultExpanded: AccordionProps<Multiple>['defaultExpanded'];
  multiple: Multiple;
  onExpandChange: AccordionProps<Multiple>['onExpandChange'];
}

/** @internal */
export interface AccordionState {
  expanded: Set<string | number>;
}

/**
 * Helper function that takes the expanded generic type and converts it
 * into an internal state.
 */
function convertPropToState<Multiple extends boolean>(
  expanded: Multiple extends true ? MultiExpanded : SingleExpanded,
): AccordionState {
  const arrayExpand = Array.isArray(expanded) ? expanded : [expanded];
  const filteredExpand = arrayExpand.filter(
    (item) => item !== '' && item !== undefined,
  );

  return {
    expanded: new Set<string | number>([...filteredExpand]),
  };
}

/** Helper function to compare the internal values of two sets. */
function isEqual(
  setA: Set<string | number>,
  setB: Set<string | number>,
): boolean {
  if (setA.size !== setB.size) {
    return false;
  }
  return [...setA].every((value) => setB.has(value));
}

/**
 * Accordion state controller
 * @internal
 */
export function useAccordionState<Multiple extends boolean>({
  expanded: expandedProp,
  defaultExpanded: defaultExpandedProp,
  multiple: multipleProp,
  onExpandChange: onExpandChangeProp,
}: useAccordionStateProps<Multiple>): [
  AccordionState,
  (action: AccordionReducerActions) => void,
] {
  const isControlled = expandedProp !== undefined;

  /** Emits the the onChange with the proper value */
  function emitOnChange(expandedState: AccordionState['expanded']) {
    const expandedSection = Array.from(expandedState);
    if (multipleProp === false) {
      onExpandChangeProp?.(
        // Falls back to an empty string to not switch between controlled and uncontrolled
        (expandedSection[0] ?? '') as OnChangeValue<Multiple>,
      );
    } else {
      onExpandChangeProp?.(expandedSection as OnChangeValue<Multiple>);
    }
  }

  /**
   * Accordion state reducer that will handle all state updates or manipulations.
   */
  function stateReducer(
    state: AccordionState,
    action: AccordionReducerActions,
  ) {
    const newExpanded = new Set(state.expanded);
    if (action.type === AccordionActions.TOGGLE_SECTION) {
      if (multipleProp === false && !newExpanded.has(action.payload)) {
        newExpanded.clear();
      }

      if (newExpanded.has(action.payload)) {
        newExpanded.delete(action.payload);
      } else {
        newExpanded.add(action.payload);
      }

      if (!isEqual(state.expanded, newExpanded)) {
        emitOnChange(newExpanded);
      }
      if (!isControlled) {
        return {
          expanded: newExpanded,
        };
      }
    }

    // If multiple changes its value, the set will need to be consolidated
    // to accomodate. If multiple turns from true to false, all expanded except
    // the first one will be closed.
    if (action.type === AccordionActions.SET_MULTIPLE) {
      if (action.payload === false && state.expanded.size > 1) {
        // Find the first expanded section in the state and keep it open.
        // This is based on the open order in the set (insertion order) and not by
        // accordion section index.
        const firstExpanded = Array.from(state.expanded)[0];
        newExpanded.clear();
        newExpanded.add(firstExpanded);
      }

      if (!isEqual(state.expanded, newExpanded)) {
        emitOnChange(newExpanded);
      }

      if (!isControlled) {
        return {
          expanded: newExpanded,
        };
      }
    }

    return state;
  }

  // Initialize the state.
  const initExpanded = multipleProp ? [] : undefined;
  const defaultExpanded = (defaultExpandedProp ??
    initExpanded) as Multiple extends true ? MultiExpanded : SingleExpanded;

  const [uncontrolledState, uncontrolledDispatch] = useReducer(
    stateReducer,
    defaultExpanded,
    convertPropToState,
  );

  // Unify the state depending on the fact if it is controlled or uncontrolled.
  const state: AccordionState = isControlled
    ? convertPropToState(expandedProp)
    : uncontrolledState;

  // This is the dispatcher proxy to make sure that controlled versions stay controlled.
  function controlledDispatch(action: AccordionReducerActions) {
    stateReducer(state, action);
  }

  return [state, isControlled ? controlledDispatch : uncontrolledDispatch];
}
